import api from "@axios";
import {
  isMockEnabled,
  mockValidateVAT,
  mockPreview,
  mockUpdateBilling,
  mockDownloadPdf,
} from "@/libs/billing-mock.js";

class Billing {
  /**
   * Validate an EU VAT ID via backend proxy to VIES (real-time).
   * Phase 4 backend endpoint is live on alpha.
   * @param {{country: string, vat_id: string}} payload — country uses VIES code (EL for Greece, etc.)
   * @returns {Promise<{valid: boolean, name?: string, address?: string, error?: string}>}
   */
  static async validateVAT(payload) {
    const { data } = await api.post("/billing/validate-vat", payload);
    return data;
  }

  /**
   * Returns ISO-2 country code derived from Cloudflare CF-IPCountry header.
   * Used to pre-fill the billing country on first visit.
   * @returns {Promise<string|null>}
   */
  static async detectIpCountry() {
    try {
      const { data } = await api.get("/billing/ip-country");
      return data?.code || null;
    } catch (_e) {
      return null;
    }
  }

  /**
   * Compute VAT/total preview for a deposit before triggering payment.
   * @param {{amount: number, billing: object}} payload
   * @returns {Promise<{subtotal:number, tax_rate:number, tax_amount:number, total:number, reverse_charge:boolean, vat_note:string|null, blocked_reason:string|null}>}
   */
  static async preview(payload) {
    if (isMockEnabled()) return mockPreview(payload);
    const { data } = await api.post("/invoices/preview", payload);
    return data;
  }

  /**
   * Save current user's default billing details.
   * Always hits the real backend (Phase 2 endpoint is live on alpha).
   * @param {object} billing
   */
  static async updateBilling(billing) {
    const { data } = await api.put("/users/me/billing", billing);
    return data;
  }

  /**
   * List the current user's tax invoices (newest first).
   * @returns {Promise<Array>}
   */
  static async listTaxInvoices() {
    const { data } = await api.get("/tax-invoices");
    return data;
  }

  /**
   * Download a tax invoice PDF (returns a Blob).
   * @param {number|string} invoiceId
   * @returns {Promise<Blob>}
   */
  static async downloadPdf(invoiceId) {
    const { data } = await api.get(`/tax-invoices/${invoiceId}/pdf`, {
      responseType: "blob",
    });
    return data;
  }

  /**
   * Download a tax invoice e-Factura UBL XML (returns a Blob).
   * @param {number|string} invoiceId
   * @returns {Promise<Blob>}
   */
  static async downloadXml(invoiceId) {
    const { data } = await api.get(`/tax-invoices/${invoiceId}/xml`, {
      responseType: "blob",
    });
    return data;
  }

  /**
   * Admin only — list all tax invoices in a date range.
   * @param {{from?: string, to?: string}} range  YYYY-MM-DD strings (optional)
   * @returns {Promise<Array>}
   */
  static async adminListTaxInvoices(range = {}) {
    const { data } = await api.get("/tax-invoices/admin/list", { params: range });
    return data;
  }

  /**
   * Admin only — bulk export e-Factura XMLs in a date range.
   * Backend tries ZIP → tar.gz → JSON fallback (in that order).
   * The caller receives the raw axios response so it can inspect headers
   * (to determine the right filename + handle the JSON fallback).
   *
   * @param {{from: string, to: string}} range
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  static async exportTaxInvoicesXmlBatch(range) {
    return api.get("/tax-invoices/export-xml", {
      params: range,
      responseType: "blob",   // works for ZIP/tar.gz; JSON also comes as Blob, parsed at the call site
      validateStatus: (s) => s < 500, // 200 (archive) or 200 (json fallback) — both useful
    });
  }
}

export default Billing;
